import React, { useEffect, useState } from "react";
import {
  Button as MuiButton,
  Box as MuiBox,
  TextField as MuiTextField,
  CircularProgress
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import { spacing, SpacingProps } from "@mui/system";
import { Link as LinkRouter, useNavigate } from "react-router-dom";
import { Papercontainer } from "@rewardle/apps.ui.papercontainer.papercontainer";
import styled from "@emotion/styled";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import axios from "axios";
import ToggleWithPopup from "./ToggleWithPopup";
import Dropdown from "../../components/dropdown/Dropdown";
import { positionOptions } from "../../Utils/commonConstants";
import { useAppSelector } from "../../redux/hooks";
const Box = styled(MuiBox)`
  display: flex;
  gap: 28px;
  margin-top: 32px;
`;

const TextField = styled(MuiTextField)`
  display: flex;
  padding-top: 1px;
  justify-content: center;
  align-items: center label {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px;
    letter-spacing: 0.15px;
  }

  input {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.15px;
    &::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
    &:hover:not(.Mui-disabled)::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
  }
`;

interface ButtonProps extends SpacingProps {
  component?: React.ElementType;
  to?: string;
  target?: string;
}
const Button = styled(MuiButton)<ButtonProps>(spacing);

const CustomButton: any = styled(MuiButton)`
  margin-top: 30px;
  &.blue_button {
    background-color: blue;
  }

  &.custom_disabled {
    background-color: grey !important;
  }
`;

function StaffDetails() {
  const paramStaffId = window.location.pathname.substring(
    window.location.pathname.lastIndexOf("/") + 1,
    window.location.pathname.length
  );
  let currentBusiness: any = localStorage?.getItem("currentBusiness");
  const state: any = {
    token: localStorage?.getItem("businessAccessToken"),
    currentBusiness: currentBusiness ? JSON.parse(currentBusiness) : null
  };
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const [staffDetails, setStaffDetails] = useState<any>({
    FirstName: "",
    LastName: "",
    Position: "",
    Mobile: "",
    Pin: "",
    Email: ""
  });

  const [validationErrors, setValidationErrors] = useState<any>({
    FirstName: "",
    LastName: "",
    Position: "",
    Mobile: "",
    Pin: "",
    Email: ""
  });

  const [loading, setiSLoading] = useState(false);

  const [errorMessageNotification, setErrorNotification] = useState<any>({
    message: null,
    color: null
  });

  // Global billing / restriction state from Redux
  const { isNonPaying, isBillingLoading } = useAppSelector(
    (s) => s.businessAccess
  );

  const postStaffDetails = (methodType: string) => {
    setiSLoading(true);
    let data = JSON.stringify(staffDetails);

    let config = {
      method: methodType,
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_API_BASE_URL}/datacollectionbusiness/business/staff?businessid=${state?.currentBusiness?.BusinessId}`,
      headers: {
        Authorization: "Bearer " + state?.token,
        "Content-Type": "application/json"
      },
      data: data
    };

    axios
      .request(config)
      .then((response) => {
        navigate("/staffs");
        setiSLoading(false);
      })
      .catch((error) => {
        console.log(
          "🚀 ~ file: StaffDetails.tsx:128 ~ postStaffDetails ~ error:",
          error?.response?.data?.errors?.join(",")
        );

        setErrorNotification({
          message: error?.response?.data?.errors?.join(","),
          color: "error"
        });
        setiSLoading(false);
      });
  };

  useEffect(() => {
    if (paramStaffId && paramStaffId !== "staff") {
      let data: any = localStorage.getItem("staffDetails");
      let staffDetailsData = JSON.parse(data);
      setStaffDetails(staffDetailsData);
    }
  }, [paramStaffId]);

  const handleToggle: any = (e: any) => {
    setStaffDetails({ ...staffDetails, IsActive: e?.target?.checked });
    setOpen(!open);
  };

  useEffect(() => {
    setOpen(staffDetails?.IsActive);
  }, [staffDetails?.IsActive]);

  const validateField = (field: string, value: string) => {
    const validations: { [key: string]: () => string } = {
      FirstName: () => (!value ? "First Name cannot be empty" : ""),
      LastName: () => (!value ? "Last Name cannot be empty" : ""),
      Mobile: () => (!value ? "Mobile cannot be empty" : ""),
      Position: () => (!value ? "Position cannot be empty" : ""),
      Pin: () => {
        if (!value) return "Pin cannot be empty";
        if (value.length !== 4) return "Only up to 4 digits are allowed";
        return "";
      },
      Email: () => {
        if (!value) return "Email cannot be empty";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Invalid email format";
        return "";
      }
    };

    const errorMessage = validations[field] ? validations[field]() : "";

    setValidationErrors((prev: any) => ({ ...prev, [field]: errorMessage }));
  };

  const handleInputChange = (field: string, value: string) => {
    setStaffDetails({ ...staffDetails, [field]: value });
    validateField(field, value);
  };

  const handlePinKeyPress = (e: {
    which: any;
    keyCode: any;
    preventDefault: () => void;
  }) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (
      (charCode < 48 || charCode > 57) && // Allow numeric characters
      charCode !== 101 && // Allow 'e'
      charCode !== 69 // Allow 'E'
    ) {
      e.preventDefault();
    }
  };

  const checkIsDisableButton = () => {
    if (
      !staffDetails?.FirstName ||
      !staffDetails?.LastName ||
      !staffDetails?.Position ||
      !staffDetails?.Mobile ||
      !staffDetails?.Pin ||
      !staffDetails?.Email
    ) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <React.Fragment>
      <Breadcrumbs
        heading="Staff"
        subHeading="Create/Update Staff"
        docs={
          <Button
            component={LinkRouter}
            to="/staffs"
            variant="contained"
            color="secondary"
            mt={2}
            className="blue_button"
          >
            Back
          </Button>
        }
      />
      <Papercontainer elevation={0}>
        {isNonPaying && (
          <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
            <Alert severity="error">
              Access limited: Please renew your contract or fix payment issues
              to manage staff.
            </Alert>
          </Stack>
        )}
        {errorMessageNotification?.message &&
          errorMessageNotification?.color && (
            <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
              <Alert severity={errorMessageNotification?.color}>
                {errorMessageNotification?.message}
              </Alert>
            </Stack>
          )}

        {paramStaffId && paramStaffId !== "staff" && (
          <ToggleWithPopup
            staffDetails={staffDetails}
            open={open}
            handleToggle={handleToggle}
          />
        )}
        <Box display={"flex"} flexDirection={"row"}>
          <TextField
            id="standard-version-input"
            label="First Name"
            type="text"
            autoComplete="current-version"
            variant="standard"
            value={staffDetails?.FirstName}
            onChange={(e) => handleInputChange("FirstName", e.target.value)}
            fullWidth={true}
            size="medium"
            error={!!validationErrors.FirstName}
            // helperText={validationErrors.FirstName}
          />
          <TextField
            id="standard-version-input"
            label="Last Name"
            type="text"
            autoComplete="current-version"
            variant="standard"
            value={staffDetails?.LastName}
            onChange={(e) => handleInputChange("LastName", e.target.value)}
            fullWidth={true}
            size="medium"
            error={!!validationErrors.LastName}
            // helperText={validationErrors.LastName}
          />
          <TextField
            id="standard-Mobile-input"
            label="Mobile"
            type="number"
            autoComplete="current-Mobile"
            variant="standard"
            value={staffDetails?.Mobile}
            onChange={(e: { target: { value: string } }) =>
              handleInputChange("Mobile", e.target.value)
            }
            inputMode="numeric"
            fullWidth={true}
            size="medium"
            error={!!validationErrors.Mobile}
            // helperText={validationErrors.Email}
          />
        </Box>

        <Box display={"flex"} flexDirection={"row"}>
          <Dropdown
            title={"Position"}
            name={"Position"}
            options={positionOptions}
            value={staffDetails?.Position}
            label={"Position"}
            minWidth={200}
            callback={(e: any) => {
              handleInputChange("Position", e.target.value);
            }}
          />
          <TextField
            id="standard-version-input"
            label="Pin"
            type="text"
            autoComplete="current-version"
            variant="standard"
            value={staffDetails?.Pin}
            onChange={(e) => handleInputChange("Pin", e.target.value)}
            onKeyPress={handlePinKeyPress}
            inputMode="numeric"
            fullWidth={true}
            size="medium"
            error={!!validationErrors.Pin}
            // helperText={validationErrors.Pin}
          />
          <TextField
            id="standard-version-input"
            label="Email"
            type="text"
            autoComplete="current-version"
            variant="standard"
            value={staffDetails?.Email}
            onChange={(e) => handleInputChange("Email", e.target.value)}
            fullWidth={true}
            size="medium"
            error={!!validationErrors.Email}
            // helperText={validationErrors.Email}
          />
        </Box>

        <CustomButton
          variant="contained"
          color="secondary"
          mt={4}
          className={`blue_button ${
            Object.values(validationErrors).some((error) => !!error) ||
            loading ||
            checkIsDisableButton() ||
            isNonPaying ||
            isBillingLoading
              ? "custom_disabled"
              : ""
          }`}
          onClick={() =>
            paramStaffId && paramStaffId !== "staff"
              ? postStaffDetails("put")
              : postStaffDetails("post")
          }
          disabled={
            Object.values(validationErrors).some((error) => !!error) ||
            loading ||
            checkIsDisableButton() ||
            isNonPaying ||
            isBillingLoading
          }
        >
          {loading ? (
            <CircularProgress size={25} color="inherit" />
          ) : paramStaffId && paramStaffId !== "staff" ? (
            "Update"
          ) : (
            "Save"
          )}
        </CustomButton>
      </Papercontainer>
    </React.Fragment>
  );
}

export default StaffDetails;
